/**
 * ProviderNode Component
 * Individual cloud provider box for the flow visualization
 */

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type CloudProvider = 'anthropic' | 'azure' | 'gcp' | 'aws';

interface ProviderNodeProps {
  provider: CloudProvider;
  isActive: boolean;
  showLabel?: boolean;
  className?: string;
}

const providerConfig: Record<
  CloudProvider,
  { name: string; color: string; glowColor: string }
> = {
  anthropic: {
    name: 'Anthropic',
    color: '#D97706',
    glowColor: 'rgba(217, 119, 6, 0.5)',
  },
  azure: {
    name: 'Azure',
    color: '#0078D4',
    glowColor: 'rgba(0, 120, 212, 0.5)',
  },
  gcp: {
    name: 'GCP',
    color: '#4285F4',
    glowColor: 'rgba(66, 133, 244, 0.5)',
  },
  aws: {
    name: 'AWS',
    color: '#FF9900',
    glowColor: 'rgba(255, 153, 0, 0.5)',
  },
};

// Provider logo SVG components
function AnthropicLogo() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z"
      />
    </svg>
  );
}

function AzureLogo() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        d="M5.483 21.3H24L14.025 4.013l-3.038 8.347 5.836 6.938L5.483 21.3ZM13.23 2.7L6.105 8.677 0 19.253h5.505l7.725-16.553Z"
      />
    </svg>
  );
}

function GCPLogo() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        d="M12.19 2.38a9.344 9.344 0 0 0-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.11-.247 10.941l.006-.007-.007.03a6.717 6.717 0 0 0 4.077 1.356h5.173l.03.03h5.192c6.687.053 9.376-8.605 3.835-12.35a9.365 9.365 0 0 0-8.825-6.893zM8.073 19.32a4.387 4.387 0 0 1-2.412-7.219L11.1 6.663a7.035 7.035 0 0 1 7.941-.502l-2.986 2.986a3.646 3.646 0 0 0-4.086.259l-5.439 5.438a4.367 4.367 0 0 1-.457-5.455l-2.986 2.986a7.045 7.045 0 0 1 .26-4.087l-2.987 2.987a4.386 4.386 0 0 1 7.219 2.412l5.438-5.438a4.387 4.387 0 0 1-2.412 7.219l-2.986 2.986c1.299.413 2.712.26 3.887-.457l-5.438-5.438a3.647 3.647 0 0 0-.259 4.086l-2.986-2.986a7.046 7.046 0 0 1 4.087-.26l-2.986-2.986a4.386 4.386 0 0 1-2.412-7.219l5.438 5.438a4.386 4.386 0 0 1 7.219 2.412L16.8 16.77a7.035 7.035 0 0 1-.502 7.941l-5.438-5.438a4.386 4.386 0 0 1-2.786-1.953z"
      />
    </svg>
  );
}

function AWSLogo() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 0 1-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 0 1-.287-.375 6.18 6.18 0 0 1-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.031-.863.103-.295.072-.583.16-.862.272a2.287 2.287 0 0 1-.28.104.488.488 0 0 1-.127.023c-.112 0-.168-.08-.168-.247v-.391c0-.128.016-.224.056-.28a.597.597 0 0 1 .224-.167c.279-.144.614-.264 1.005-.36a4.84 4.84 0 0 1 1.246-.151c.95 0 1.644.216 2.091.647.439.43.662 1.085.662 1.963v2.586zm-3.24 1.214c.263 0 .534-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 0 0-.735-.136 6.02 6.02 0 0 0-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.144 0-.24-.024-.304-.08-.064-.048-.12-.16-.168-.311L7.586 5.55a1.398 1.398 0 0 1-.072-.32c0-.128.064-.2.191-.2h.783c.151 0 .255.025.31.08.065.048.113.16.16.312l1.342 5.284 1.245-5.284c.04-.16.088-.264.151-.312a.549.549 0 0 1 .32-.08h.638c.152 0 .256.025.32.08.063.048.12.16.151.312l1.261 5.348 1.381-5.348c.048-.16.104-.264.16-.312a.52.52 0 0 1 .311-.08h.743c.127 0 .2.065.2.2 0 .04-.009.08-.017.128a1.137 1.137 0 0 1-.056.2l-1.923 6.17c-.048.16-.104.263-.168.311a.51.51 0 0 1-.303.08h-.687c-.151 0-.255-.024-.32-.08-.063-.056-.119-.16-.15-.32l-1.238-5.148-1.23 5.14c-.04.16-.087.264-.15.32-.065.056-.177.08-.32.08zm10.256.215c-.415 0-.83-.048-1.229-.143-.399-.096-.71-.2-.918-.32-.128-.071-.215-.151-.247-.223a.563.563 0 0 1-.048-.224v-.407c0-.167.064-.247.183-.247.048 0 .096.008.144.024.048.016.12.048.2.08.271.12.566.215.878.279.319.064.63.096.95.096.502 0 .894-.088 1.165-.264a.86.86 0 0 0 .415-.758.777.777 0 0 0-.215-.559c-.144-.151-.416-.287-.807-.415l-1.157-.36c-.583-.183-1.014-.454-1.277-.813a1.902 1.902 0 0 1-.4-1.158c0-.335.073-.63.216-.886.144-.255.335-.479.575-.654.24-.184.51-.32.83-.415.32-.096.655-.136 1.006-.136.175 0 .359.008.535.032.183.024.35.056.518.088.16.04.312.08.455.127.144.048.256.096.336.144a.69.69 0 0 1 .24.2.43.43 0 0 1 .071.263v.375c0 .168-.064.256-.184.256a.83.83 0 0 1-.303-.096 3.652 3.652 0 0 0-1.532-.311c-.455 0-.815.071-1.062.223-.248.152-.375.383-.375.71 0 .224.08.416.24.567.159.152.454.304.877.44l1.134.358c.574.184.99.44 1.237.767.247.327.367.702.367 1.117 0 .343-.072.655-.207.926-.144.272-.336.511-.583.703-.248.2-.543.343-.886.447-.36.111-.734.167-1.142.167zM21.698 16.207c-2.626 1.94-6.442 2.969-9.722 2.969-4.598 0-8.74-1.7-11.87-4.526-.247-.223-.024-.527.272-.351 3.384 1.963 7.559 3.153 11.877 3.153 2.914 0 6.114-.607 9.06-1.852.439-.2.814.287.383.607zM22.792 14.961c-.336-.43-2.22-.207-3.074-.103-.255.032-.295-.192-.063-.36 1.5-1.053 3.967-.75 4.254-.399.287.36-.08 2.826-1.485 4.007-.215.184-.423.088-.327-.151.32-.79 1.03-2.57.695-2.994z"
      />
    </svg>
  );
}

const providerLogos: Record<CloudProvider, React.ReactNode> = {
  anthropic: <AnthropicLogo />,
  azure: <AzureLogo />,
  gcp: <GCPLogo />,
  aws: <AWSLogo />,
};

export function ProviderNode({
  provider,
  isActive,
  showLabel = true,
  className,
}: ProviderNodeProps) {
  const config = providerConfig[provider];

  return (
    <motion.g
      className={cn('provider-node', className)}
      initial={false}
      animate={isActive ? 'active' : 'inactive'}
    >
      {/* Glow filter definition */}
      <defs>
        <filter id={`glow-${provider}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Background rect with glow */}
      <motion.rect
        width="100"
        height="60"
        rx="8"
        fill="hsl(var(--card))"
        stroke={isActive ? config.color : 'hsl(var(--border))'}
        strokeWidth={isActive ? 2 : 1}
        filter={isActive ? `url(#glow-${provider})` : 'none'}
        initial={false}
        animate={{
          stroke: isActive ? config.color : 'hsl(var(--border))',
          strokeWidth: isActive ? 2 : 1,
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Provider icon container */}
      <foreignObject x="10" y="10" width="30" height="30">
        <div
          className="flex items-center justify-center w-full h-full"
          style={{ color: isActive ? config.color : 'hsl(var(--muted-foreground))' }}
        >
          {providerLogos[provider]}
        </div>
      </foreignObject>

      {/* Provider name */}
      {showLabel && (
        <text
          x="50"
          y="38"
          textAnchor="middle"
          className="text-xs font-medium"
          fill={isActive ? config.color : 'hsl(var(--foreground))'}
        >
          {config.name}
        </text>
      )}

      {/* Active indicator dot */}
      {isActive && (
        <motion.circle
          cx="90"
          cy="10"
          r="4"
          fill={config.color}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
      )}
    </motion.g>
  );
}

export { providerConfig };
